import { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, LogIn } from "lucide-react";
import {
  LOGO_URL,
  HEADER_NAV_GROUPS,
  INVESTOR_LOGIN_URL,
  INVESTOR_LOGIN_LABEL,
  WHATSAPP_LOGIN_HELP_URL,
} from "@/lib/brand";
import ThemeToggle from "@/components/ThemeToggle";

export default function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // Close mobile menu whenever the route changes
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      className="sticky top-0 z-40 bg-paper/85 backdrop-blur-md border-b hairline"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex items-center justify-between h-24">
          <Link
            to="/"
            className="flex items-center gap-3"
            data-testid="logo-link"
            onClick={() => setOpen(false)}
          >
            <img
              src={LOGO_URL}
              alt="Reliserve Wealth"
              className="h-16 w-auto md:h-[68px]"
            />
          </Link>

          {/* Desktop nav */}
          <nav
            className="hidden lg:flex items-center gap-1"
            data-testid="primary-nav"
          >
            {HEADER_NAV_GROUPS.map((group) => (
              <NavGroup key={group.label} group={group} pathname={pathname} />
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-2">
            <ThemeToggle testId="theme-toggle-desktop" />
            <a
              href="https://reliservepulse.com/book-consultation"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-brass"
              data-testid="header-cta-talk"
            >
              Talk to us
            </a>
            <div className="relative">
              <a
                href={INVESTOR_LOGIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${INVESTOR_LOGIN_LABEL} (opens in a new tab)`}
                className="btn-teal inline-flex items-center gap-1.5"
                data-testid="header-cta-investor-login"
              >
                <LogIn size={14} />
                {INVESTOR_LOGIN_LABEL}
              </a>
              <LoginHelpLink
                className="absolute left-1/2 -translate-x-1/2 top-full mt-1"
                testId="header-login-help-link"
              />
            </div>
          </div>

          {/* Mobile controls */}
          <div className="lg:hidden flex items-center gap-2">
            <ThemeToggle testId="theme-toggle-mobile" />
            <button
              className="text-ink p-2"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {open && (
          <div
            className="lg:hidden pb-6 pt-2 border-t hairline"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col divide-y hairline">
              {HEADER_NAV_GROUPS.map((group) => (
                <MobileGroup
                  key={group.label}
                  group={group}
                  pathname={pathname}
                  onItemClick={() => setOpen(false)}
                />
              ))}
            </div>
            <div className="flex flex-col gap-2 mt-5">
              <a
                href="https://reliservepulse.com/book-consultation"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-brass w-full justify-center"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta-talk"
              >
                Talk to us
              </a>
              <a
                href={INVESTOR_LOGIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${INVESTOR_LOGIN_LABEL} (opens in a new tab)`}
                className="btn-teal w-full justify-center inline-flex items-center gap-1.5"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta-investor-login"
              >
                <LogIn size={14} />
                {INVESTOR_LOGIN_LABEL}
              </a>
              <LoginHelpLink
                className="self-center"
                testId="mobile-login-help-link"
              />
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

/* ---------------- Desktop dropdown group ---------------- */
function NavGroup({ group, pathname }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const closeTimer = useRef(null);
  const isActive = group.items.some((it) => matchPath(pathname, it.to));
  const testId = `nav-${slug(group.label)}`;

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Close on route change
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };
  const cancelClose = () => clearTimeout(closeTimer.current);

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={testId}
        className={
          "inline-flex items-center gap-1 px-3 py-2 text-[14px] tracking-wide transition-colors " +
          (isActive
            ? "text-brass-deep font-medium"
            : "text-ink-soft hover:text-ink")
        }
      >
        {group.label}
        <ChevronDown
          size={14}
          className={"transition-transform " + (open ? "rotate-180" : "")}
        />
      </button>
      {open && (
        <div
          role="menu"
          data-testid={`${testId}-panel`}
          className="
            absolute left-0 top-full mt-2 min-w-[220px]
            card-paper rounded-md shadow-lg py-2
            z-50
          "
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {group.items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              role="menuitem"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                "block px-4 py-2 text-[14px] transition-colors " +
                (isActive
                  ? "text-brass-deep font-medium bg-paper-raised"
                  : "text-ink-soft hover:text-ink hover:bg-paper-raised")
              }
              data-testid={`nav-${slug(group.label)}-${slug(it.label)}`}
            >
              {it.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Mobile accordion group ---------------- */
function MobileGroup({ group, pathname, onItemClick }) {
  const isActive = group.items.some((it) => matchPath(pathname, it.to));
  const [open, setOpen] = useState(isActive);
  const testId = `mobile-nav-${slug(group.label)}`;

  return (
    <div data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={
          "w-full flex items-center justify-between py-3 px-2 text-[15px] " +
          (isActive ? "text-brass-deep font-medium" : "text-ink-soft")
        }
      >
        <span>{group.label}</span>
        <ChevronDown
          size={16}
          className={"transition-transform " + (open ? "rotate-180" : "")}
        />
      </button>
      {open && (
        <div className="flex flex-col pb-2">
          {group.items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              onClick={onItemClick}
              className={({ isActive }) =>
                "py-2 pl-5 pr-2 text-[14px] " +
                (isActive ? "text-brass-deep font-medium" : "text-ink-soft")
              }
              data-testid={`${testId}-${slug(it.label)}`}
            >
              {it.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Helpers ---------------- */
function slug(s) {
  return s.toLowerCase().replace(/\s+/g, "-");
}
function matchPath(current, target) {
  if (target === "/") return current === "/";
  return current === target || current.startsWith(target + "/");
}


function LoginHelpLink({ className, testId }) {
  return (
    <a
      href={WHATSAPP_LOGIN_HELP_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Need help logging in? Chat with us on WhatsApp (opens in a new tab)"
      className={`whitespace-nowrap text-[11px] text-ink-soft hover:text-[hsl(var(--brand-teal))] hover:underline underline-offset-2 transition-colors ${className}`}
      data-testid={testId}
    >
      Need help logging in?
    </a>
  );
}
